import { useCallback, useState } from 'react';

import useCompanyContext from 'common/hooks/useCompanyContext';
import useErrorHandledMutation, {
  throwOnlyUnhandledErrors,
} from 'common/hooks/useErrorHandledMutation';

import {
  shineStartDocumentQuery,
  updateShineStartDocumentMutation,
} from './graphql';
import {
  ShineStartDocumentMutationHook,
  UpdateShineStartDocumentFn,
  UpdateShineStartDocumentVariables,
} from './types';

const useShineStartDocumentMutation: ShineStartDocumentMutationHook = ({
  shineStartDocumentId,
  shineStartId,
}) => {
  const { companyProfileId } = useCompanyContext();
  const [isDeletingDocument, setIsDeletingExistingDocument] = useState(false);
  const [isUpdatingDocument, setIsUpdatingExistingDocument] = useState(false);
  // TODO(matt): Update any queries outside VerificationUploadShineStartDoc that depend on the current
  // state of the ShineStartDocuments
  const [mutation] = useErrorHandledMutation<
    unknown,
    UpdateShineStartDocumentVariables
  >(updateShineStartDocumentMutation, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: shineStartDocumentQuery,
        variables: {
          companyProfileId,
        },
      },
    ],
  });

  const deleteDocument = useCallback(() => {
    if (shineStartId === undefined || shineStartDocumentId === undefined) {
      throw new Error(
        'Only possible to delete once shine start document has loaded',
      );
    }
    setIsDeletingExistingDocument(true);
    const input = {
      firebaseBucket: '',
      firebaseRef: '',
      shineStartDocumentId,
      shineStartId,
      url: '',
    };
    return mutation({
      variables: { input },
    })
      .catch(throwOnlyUnhandledErrors)
      .then(() => {
        setIsDeletingExistingDocument(false);
      });
  }, [shineStartDocumentId, shineStartId, mutation]);

  const updateDocument = useCallback<UpdateShineStartDocumentFn>(
    (args) => {
      if (shineStartId === undefined || shineStartDocumentId === undefined) {
        throw new Error(
          'Only possible to upload once shine start document has loaded',
        );
      }
      setIsUpdatingExistingDocument(true);
      const input = {
        shineStartDocumentId,
        shineStartId,
        ...args,
      };
      return mutation({
        variables: { input },
      })
        .catch(throwOnlyUnhandledErrors)
        .then(() => {
          setIsUpdatingExistingDocument(false);
        });
    },
    [shineStartDocumentId, shineStartId, mutation],
  );

  return {
    deleteDocument,
    isDeletingDocument,
    isUpdatingDocument,
    updateDocument,
  };
};

export default useShineStartDocumentMutation;
